import React, { useState, useEffect } from 'react';
import { Package, Warehouse, TrendingUp, Plus, Edit, X, Trash2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import './InventoryPage.css'

export default function InventoryManagement() {
  const { user, token, logout } = useAuth();
  const REACT_APP_API_URL = process.env.REACT_APP_API_BASE_URL;

  const [activeTab, setActiveTab] = useState('inventory');
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [showStockAdjustment, setShowStockAdjustment] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);

  const [inventoryForm, setInventoryForm] = useState({
    productId: '', warehouseId: '', quantity: '', reorderPoint: ''
  });

  const [inventory, setInventory] = useState([]);

  const fetchInventory = async (forceFetch = false) => {
    if(!forceFetch){
      const cachedInventory = sessionStorage.getItem("inventory");
      if(cachedInventory){
        setInventory(JSON.parse(cachedInventory));
        return;
      }
    }
    
    try {
      const res = await fetch(`${REACT_APP_API_URL}/Inventory`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error("Failed to fetch inventory:", res.status);
        return;
      }

      const data = await res.json();
       
      setInventory(data);
      sessionStorage.setItem("inventory", JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching inventory:", err);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchInventory();
  }, [token]);

 
  const [adjustmentForm, setAdjustmentForm] = useState({
    inventoryId: '', newQuantity: '', reorderPoint: '', reason: ''
  });

  const [stockAdjustments, setStockAdjustments] = useState([]);

  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
  if (!token) return;

  const fetchData = async () => {
    try {
      //Try to read cached products & warehouses
      const cachedProducts = sessionStorage.getItem("products");
      const cachedWarehouses = sessionStorage.getItem("warehouses");

      if (cachedProducts && cachedWarehouses) {
        // Load from cache immediately
        setProducts(JSON.parse(cachedProducts));
        setWarehouses(JSON.parse(cachedWarehouses));
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const [productsRes, warehousesRes] = await Promise.all([
        fetch(`${REACT_APP_API_URL}/Products`, { headers }),
        fetch(`${REACT_APP_API_URL}/warehouse`, { headers }),
      ]);

      if (!productsRes.ok || !warehousesRes.ok) {
        throw new Error("Failed to fetch one or more resources");
      }

      const [productsData, warehousesData] = await Promise.all([
        productsRes.json(),
        warehousesRes.json(),
      ]);

      setProducts(productsData);
      setWarehouses(warehousesData);

      sessionStorage.setItem("products", JSON.stringify(productsData));
      sessionStorage.setItem("warehouses", JSON.stringify(warehousesData));
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  fetchData();
}, [token]);

  const [totalStockUnits, setTotalStockUnits] = useState(0);
  const [lowStockItems, setLowStockItems] = useState(0);
  const [lowStockList, setLowStockList] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);

  
  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;

      try {
        const headers = {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        };

        const [
          totalStockRes,
          lowStockCountRes,
          lowStockListRes,
          alertsRes,
        ] = await Promise.all([
          fetch(`${REACT_APP_API_URL}/Inventory/total-stock-units`, { headers }),
          fetch(`${REACT_APP_API_URL}/Inventory/low-stock/count`, { headers }),
          fetch(`${REACT_APP_API_URL}/inventory/low-stock`, { headers }),
          fetch(`${REACT_APP_API_URL}/Inventory/alerts`, { headers }),
        ]);

        const [totalStockData, lowStockCountData, lowStockListData, alertsData] =
          await Promise.all([
            totalStockRes.json(),
            lowStockCountRes.json(),
            lowStockListRes.json(),
            alertsRes.json(),
          ]);

        setTotalStockUnits(totalStockData);
        setLowStockItems(lowStockCountData.count);
        setLowStockList(lowStockListData);
        setActiveAlerts(alertsData);
      } catch (err) {
        console.error("Error fetching inventory data:", err);
      }
    };

    fetchAll();
  }, [token]);

  // Function to handle going back to dashboard
  const handleBackToDashboard = () => {
    // You can replace this with your actual navigation logic
     
    window.location.href = '/dashboard';
  };

  const handleInventorySubmit = async (e) => {
    e.preventDefault();

    //Find matching Product and Warehouse objects
    const selectedProduct = products.find(
      (p) => p.sku === inventoryForm.productId || p.name === inventoryForm.productId
    );
    const selectedWarehouse = warehouses.find(
      (w) => w.warehouseCode === inventoryForm.warehouseId
    );

    if (!selectedProduct || !selectedWarehouse) {
      alert("Invalid product or warehouse selected");
      return;
    }

    const payload = {
      productId: selectedProduct.id,
      warehouseId: selectedWarehouse.id,
    }

     
     
    
    try {
      
      const res = await fetch(`${REACT_APP_API_URL}/Inventory/placeholder`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)  
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const savedItem = await res.json();

      setProducts(prev => {
        const updated = [...prev, savedItem];

        // Update the sessionStorage cache
        sessionStorage.setItem("inventory", JSON.stringify(updated));

        return updated;
      });

      await fetchInventory(true);
    
      // Reset form and hide modal
      setInventoryForm({ productId: "", warehouseId: "", quantity: "", reorderPoint: "" });
      setShowInventoryForm(false);
    } catch (err) {
      console.error("Failed to save inventory:", err);
      alert("Error saving inventory: " + err.message);
    }
  };


  const handleStockAdjustment = async (e) => {
    e.preventDefault();
    
    const selectedInventory = inventory.find(i => i.id === adjustmentForm.inventoryId);
    if (!selectedInventory) return;

    const oldQuantity = selectedInventory.units;
    const newQuantity = parseInt(adjustmentForm.newQuantity);
    const difference = newQuantity - oldQuantity;
    const newReoderPoint = parseInt(adjustmentForm.reorderPoint);

    const adjustment = {
      id: Date.now(),
      inventoryId: parseInt(adjustmentForm.inventoryId),
      productId: selectedInventory.productId,
      warehouseId: selectedInventory.warehouseId,
      oldQuantity: oldQuantity,
      newQuantity: newQuantity,
      difference: difference,
      reason: adjustmentForm.reason,
      timestamp: new Date().toISOString()
    };

    try {
      if(!token) return;
     const response = await fetch(`${REACT_APP_API_URL}/Inventory/${selectedInventory.id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ units: newQuantity, reorderPoint: newReoderPoint })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error updating inventory:", errorText);
        alert("Failed to update stock.");
        return;
      }

      setStockAdjustments([adjustment, ...stockAdjustments]);

      setInventory(prev => {
        const updated = prev.map(i =>
          i.id === adjustmentForm.inventoryId
            ? { ...i, units: newQuantity, reorderPoint: newReoderPoint }
            : i
        );

        sessionStorage.setItem("inventory", JSON.stringify(updated)); // ✔ STORE THE ARRAY
        return updated;
      });

      setAdjustmentForm({ inventoryId: '', newQuantity: '', reason: '' });
      setShowStockAdjustment(false);
      alert("Stock adjusted successfully!");

    } catch (error) {
      console.error("Network error:", error);
      alert("Failed to update stock due to a network error.");
    }
  };

  const deleteInventory = async (id) => {
    try {
    await fetch(`${REACT_APP_API_URL}/Inventory/${id}`, { 
      method: 'DELETE',
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    setInventory(prev => {
      const updated = prev.filter(i => i.id !== id);

      // Update sessionStorage
      sessionStorage.setItem("inventory", JSON.stringify(updated));

      return updated;
    });
    await fetchInventory(true);
  } catch (err) {
    console.error(err);
    alert('Error deleting inventory');
  }
    setStockAdjustments(stockAdjustments.filter(a => a.inventoryId !== id));
  };

  const openEditInventory = (item) => {
    setEditingInventory(item);
    setInventoryForm({
      productId: item.productId,
      warehouseId: item.warehouseId,
      quantity: item.units.toString(),
      reorderPoint: item.reorderPoint.toString()
    });
    setShowInventoryForm(true);
  };

  const isLowStock = (item) => {
    return Number(item.units) <= Number(item.reorderPoint);
  };

  useEffect(() => {
    const lowItems = inventory?.filter(item => isLowStock(item));
    setLowStockList(lowItems);
    setLowStockItems(lowItems?.length);
  }, [inventory]);

  const getTotalStockValue = () => {
    return inventory?.reduce((sum, item) => sum + item.units, 0);
  };

  const getLowStockCount = () => {
    return inventory.filter(item => isLowStock(item)).length;
  };

  // Get unique warehouse IDs for transfer dropdown
  const getWarehouseOptions = () => {
    return [...new Set(warehouses.map(item => item.warehouseCode))];
  };

  // Get products for a specific warehouse
  const getProductsForWarehouse = (warehouseId) => {
    return [...new Set(inventory
      .filter(item => item.warehouseId === warehouseId)
      .map(item => item.productId)
    )];
  };

  const getProductSku = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.sku : productId; // fallback to id if not found
  };

  const getWarehouseCode = (warehouseId) => {
    if (!warehouseId) return "-";
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.warehouseCode : warehouseId;
  };

  const [filters, setFilters] = useState({
    search: "",
    Product: "",
    Warehouse: ""
  });
  
  const filteredInventory = (inventory || []).filter(item =>
    (!filters.search ||
      getProductSku(item.productId)?.toLowerCase().includes(filters.search.toLowerCase()) ||
      item.productName?.toLowerCase().includes(filters.search.toLowerCase())
    ) &&
    (!filters.Product || item.productId === filters.Product) &&
    (!filters.Warehouse || item.warehouseId === filters.Warehouse)
  );

  return (
    <div className="inventory-container">
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#22331D', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>
      {/* Header */}
      <div className="header-box">
        <div className="header-inner">
          <div className="header-items">
            <div className="heading-section">
              {/*<div className="logo">
                <Package style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>*/}
              <div className='heading'>
                <h1>Inventory Management</h1>
              </div>
            </div>
            <div className="header-buttons">
              <button className="back-btn"
                onClick={handleBackToDashboard}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '8px 16px', 
                  color: 'white', 
                  borderRadius: '8px', 
                  backgroundColor: '#6c757d', 
                  border: 'none', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                <ArrowLeft style={{ width: '16px', height: '16px' }} />
                Back to Dashboard
              </button>
              <button
                onClick={() => {
                  setEditingInventory(null);
                  setInventoryForm({ productId: '', warehouseId: '', quantity: '', reorderPoint: '' });
                  setShowInventoryForm(true);
                }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '8px 16px', 
                  color: 'white', 
                  borderRadius: '8px', 
                  backgroundColor: '#ff8c00', 
                  border: 'none', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                New Inventory Entry
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rest of the component remains exactly the same */}
      {/* Stats Cards */}
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 24px', marginTop: '24px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
            border: '1px solid #e5e7eb', 
            padding: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Total Inventory Items</p>
                <p style={{ fontSize: '30px', fontWeight: 'bold', marginTop: '4px', margin: 0, color: '#22331D' }}>{inventory?.length}</p>
              </div>
              <Package style={{ width: '48px', height: '48px', color: '#d1d5db' }} />
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
            border: '1px solid #e5e7eb', 
            padding: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Total Stock Units</p>
                <p style={{ fontSize: '30px', fontWeight: 'bold', marginTop: '4px', margin: 0, color: '#22331D' }}>{getTotalStockValue()}</p>
              </div>
              <TrendingUp style={{ width: '48px', height: '48px', color: '#d1d5db' }} />
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
            border: '1px solid #e5e7eb', 
            padding: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Low Stock Alerts</p>
                <p style={{ fontSize: '30px', fontWeight: 'bold', marginTop: '4px', margin: 0, color: '#dc2626' }}>{lowStockItems}</p>
              </div>
              <AlertCircle style={{ width: '48px', height: '48px', color: '#fecaca' }} />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('inventory')}
            style={{ 
              padding: '12px 24px', 
              fontWeight: '500', 
              color: activeTab === 'inventory' ? '#22331D' : '#4b5563',
              borderBottom: activeTab === 'inventory' ? '2px solid #22331D' : 'none',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Current Inventory
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 24px', paddingTop: '24px', paddingBottom: '24px' }}>
        {activeTab === 'inventory' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Real-Time Stock Level Viewing & Edition</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowStockAdjustment(true)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    padding: '8px 16px', 
                    color: 'white', 
                    borderRadius: '8px', 
                    backgroundColor: '#ff8c00', 
                    border: 'none', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.9'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  <RefreshCw style={{ width: '16px', height: '16px' }} />
                  Stock Adjustment
                </button>
              </div>
            </div>

            <div className="filters-section">
              {/* Search */}
              <div className="form-group filter-group">
                <input
                  type="text"
                  placeholder=" "
                  value={filters.search}
                  onChange={e => setFilters({ ...filters, search: e.target.value })}
                />
                <label>Search inventory...</label>
              </div>

              {/* Product filter */}
              <div className="form-group filter-group">
                <select
                  value={filters.Product}
                  onChange={e => setFilters({ ...filters, Product: e.target.value })}
                >
                  <option value="">All products</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <label>Product</label>
              </div>

              {/* Warehouse filter */}
              <div className="form-group filter-group">
                <select
                  value={filters.Warehouse}
                  onChange={e => setFilters({ ...filters, Warehouse: e.target.value })}
                >
                  <option value="">All warehouses</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <label>Warehouse</label>
              </div>
            </div>

            {filteredInventory?.length === 0 ? (
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
                padding: '48px', 
                textAlign: 'center', 
                border: '1px solid #e5e7eb' 
              }}>
                <Package style={{ width: '64px', height: '64px', margin: '0 auto', color: '#d1d5db', marginBottom: '16px' }} />
                <p style={{ color: '#6b7280', margin: 0 }}>No inventory records yet. Click "New Inventory Entry" to start tracking stock.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredInventory?.map((item) => (
                  <div key={item.id} style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
                    border: '1px solid #e5e7eb'
                  }}>
                    {isLowStock(item) && (
                      <div style={{ 
                        backgroundColor: '#fef2f2', 
                        borderBottom: '1px solid #fee2e2', 
                        padding: '8px 24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        color: '#dc2626', 
                        fontSize: '14px' 
                      }}>
                        <AlertCircle style={{ width: '16px', height: '16px' }} />
                        <span style={{ fontWeight: '500' }}>Low Stock Alert: Quantity at or below reorder point</span>
                      </div>
                    )}
                    <div style={{ padding: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '24px'
                          }}>
                            <div>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', margin: 0 }}>Product SKU</p>
                              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{getProductSku(item.productId)} ({item.productName})</p>
                              
                            </div>
                            <div>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', margin: 0 }}>Warehouse Code</p>
                              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{getWarehouseCode(item.warehouseId)} ({item.warehouseName}) </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', margin: 0 }}>Current Quantity</p>
                              <p style={{ fontSize: '24px', fontWeight: 'bold', color: isLowStock(item) ? '#dc2626' : '#22331D', margin: 0 }}>
                                {item.units}
                              </p>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', margin: 0 }}>units in stock</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', margin: 0 }}>Reorder Point</p>
                              <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151', margin: 0 }}>{item.reorderPoint} units</p>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                          {/*<button 
                            onClick={() => openEditInventory(item)} 
                            style={{ 
                              color: '#9ca3af', 
                              backgroundColor: 'transparent', 
                              border: 'none', 
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                            onMouseOver={(e) => e.target.style.color = '#4b5563'}
                            onMouseOut={(e) => e.target.style.color = '#9ca3af'}
                            title="Edit inventory record"
                          >
                            <Edit style={{ width: '20px', height: '20px' }} />
                          </button>*/}
                          <button 
                            onClick={() => deleteInventory(item.id)} 
                            style={{ 
                              color: '#9ca3af', 
                              backgroundColor: 'transparent', 
                              border: 'none', 
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                            onMouseOver={(e) => e.target.style.color = '#dc2626'}
                            onMouseOut={(e) => e.target.style.color = '#9ca3af'}
                            title="Delete inventory record"
                          >
                            <Trash2 style={{ width: '20px', height: '20px' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showInventoryForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '28rem', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                {editingInventory ? 'Edit Inventory Record' : 'New Inventory Entry'}
              </h3>
              <button 
                onClick={() => { 
                  setShowInventoryForm(false); 
                  setEditingInventory(null); 
                }} 
                style={{ color: '#9ca3af', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseOver={(e) => e.target.style.color = '#4b5563'}
                onMouseOut={(e) => e.target.style.color = '#9ca3af'}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <form onSubmit={handleInventorySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Product ID</label>
                <input
                  list="product-options"
                  type="text"
                  required
                  value={inventoryForm.productId}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, productId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                  placeholder="Enter ID or choose..."
                />

                <datalist id="product-options">
                  {products.map((p) => (
                    <option key={p.id} value={p.sku}>
                      {p.name}
                    </option>
                  ))}
                </datalist>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', margin: 0 }}>Enter the unique product identifier</p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Warehouse ID</label>
                <input
                  list="warehouse-options"
                  type="text"
                  required
                  value={inventoryForm.warehouseId}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, warehouseId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                  placeholder="Enter ID or choose..."
                />

                <datalist id="warehouse-options">
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.warehouseCode}>
                      {w.name}
                    </option>
                  ))}
                </datalist>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', margin: 0 }}>Enter the unique warehouse identifier</p>
              </div>
              {/*<div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Quantity (Current Stock)</label>
                <input
                  type="number"
                  required
                  min="0"
                  value={inventoryForm.quantity}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  placeholder="Current number of units in stock"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Reorder Point</label>
                <input
                  type="number"
                  required
                  min="0"
                  value={inventoryForm.reorderPoint}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, reorderPoint: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  placeholder="Minimum quantity before reorder"
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', margin: 0 }}>Alert when stock reaches this level</p>
              </div>*/}
              <button
                type="submit"
                style={{ width: '100%', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#ff8c00', border: 'none', cursor: 'pointer' }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                {editingInventory ? 'Update Record' : 'Create Entry'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showStockAdjustment && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '28rem', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Stock Adjustment with Reason</h3>
              <button 
                onClick={() => setShowStockAdjustment(false)} 
                style={{ color: '#9ca3af', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseOver={(e) => e.target.style.color = '#4b5563'}
                onMouseOut={(e) => e.target.style.color = '#9ca3af'}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            {inventory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>No inventory records available. Create an inventory entry first.</p>
                <button
                  onClick={() => {
                    setShowStockAdjustment(false);
                    setShowInventoryForm(true);
                  }}
                  style={{ marginTop: '16px', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#ff8c00', border: 'none', cursor: 'pointer' }}
                  onMouseOver={(e) => e.target.style.opacity = '0.9'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  Create Inventory Entry
                </button>
              </div>
            ) : (
              <form onSubmit={handleStockAdjustment} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Select Inventory Record */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px',
                    }}
                  >
                    Select Inventory Record
                  </label>

                  <input
                    list="inventoryOptions"
                    required
                    value={adjustmentForm.inventoryText || ''}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      const selected = inventory.find(
                        (i) =>
                          `${i.productName} | ${i.warehouseName}` === inputValue
                      );

                      setAdjustmentForm({
                        ...adjustmentForm,
                        inventoryId: selected ? selected.id : '',      
                        inventoryText: inputValue,                     
                        reorderPoint: selected ? selected.reorderPoint : '',
                        newQuantity: selected ? selected.units : '',
                        warehouseName: selected ? selected.warehouseName : '',
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      outline: 'none',
                    }}
                  />

                  <datalist id="inventoryOptions">
                    {inventory.map((item) => (
                      <option
                        key={item.id}
                        value={`${item.productName} | ${item.warehouseName}`} 
                      >
                        Qty: {item.units ?? 0}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px',
                    }}
                  >
                    Warehouse
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={adjustmentForm.warehouseName || ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      outline: 'none',
                      backgroundColor: '#f3f4f6',
                    }}
                    placeholder="Warehouse will appear here"
                  />
                </div>

                {/* New Quantity */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Quantity
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={adjustmentForm.newQuantity ?? ''}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, newQuantity: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                    placeholder="Enter new quantity"
                  />
                </div>

                {/* Reorder Point */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Reorder Point
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={adjustmentForm.reorderPoint ?? ''}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reorderPoint: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                    placeholder="Enter reorder point"
                  />
                </div>

                {/* Reason for Adjustment */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Reason for Adjustment
                  </label>
                  <textarea
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', resize: 'none' }}
                    rows="3"
                    placeholder="e.g., Damaged goods, Inventory recount, Loss/theft, etc."
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', margin: 0 }}>
                    Provide a clear reason for this adjustment
                  </p>
                </div>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    color: 'white',
                    borderRadius: '8px',
                    backgroundColor: '#ff8c00',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.9'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  Apply Adjustment
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}